import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { Commands } from '@vaadin/router';
import { LitPage } from '../components/lit-page';
import { isPuzzleId, type PuzzleId } from '../components/luna-common';
import { navigate } from '../navigation';
import { splitPresetLabel } from '../shell';
import { getPuzzle } from '../puzzles/puzzleBase';
import initialisePuzzleMetadata from '../puzzles/puzzleMetadata';
import { loadPresets } from '../puzzles/preset-controls';
import { getFullCatalog } from '../puzzles/settings-catalog';
import { encodePresetOptions, type Preset } from '../puzzles/preset';
import { autoName } from '../puzzles/auto-name';
import type { Options } from '../puzzles/wasmBridge/viewModel';

import '../components/luna-board-art/luna-board-art';
import '../components/luna-preset-row/luna-preset-row';
import '../components/luna-skeleton/luna-skeleton';
import '../components/luna-custom-board-card/luna-custom-board-card';

interface SavedPresetView {
  sizeLabel: string;
  detail: string;
  options: Options[];
}

type PresetRowView =
  | { kind: 'engine'; presetId: number; sizeLabel: string; detail: string }
  | { kind: 'board'; label: string; options: Options[]; sizeLabel: string; detail: string };

type Breakpoint = 'desktop' | 'tablet' | 'mobile';

const THUMB_ART: Record<Breakpoint, number> = { desktop: 216, tablet: 76, mobile: 56 };

@customElement('app-picker')
export class AppPicker extends LitPage {
  @state() private _puzzleName: PuzzleId = 'map';
  @state() private _displayName = '';
  @state() private _description = '';
  @state() private _presets: PresetRowView[] = [];
  @state() private _saved: SavedPresetView[] = [];
  @state() private _loading = true;
  @state() private _breakpoint: Breakpoint = 'desktop';
  private _levels = new Map<string, { name: string; id: number }[]>();
  private _mqMobile: MediaQueryList | null = null;
  private _mqTablet: MediaQueryList | null = null;
  private _onMq = () => this.updateBreakpoint();

  connectedCallback() {
    super.connectedCallback();
    if (typeof window.matchMedia === 'function') {
      this._mqMobile = window.matchMedia('(max-width: 767px)');
      this._mqTablet = window.matchMedia('(min-width: 768px) and (max-width: 1279px)');
      this._mqMobile.addEventListener('change', this._onMq);
      this._mqTablet.addEventListener('change', this._onMq);
      this.updateBreakpoint();
    }
  }

  disconnectedCallback() {
    this._mqMobile?.removeEventListener('change', this._onMq);
    this._mqTablet?.removeEventListener('change', this._onMq);
    super.disconnectedCallback();
  }

  private updateBreakpoint(): void {
    if (this._mqMobile?.matches) {
      this._breakpoint = 'mobile';
    } else if (this._mqTablet?.matches) {
      this._breakpoint = 'tablet';
    } else {
      this._breakpoint = 'desktop';
    }
  }

  onBeforeEnter(location: { params: { puzzleName?: string } }, commands: Commands): void {
    const name = location.params.puzzleName ?? '';
    if (!isPuzzleId(name)) {
      commands.redirect('/');
      return;
    }
    this._puzzleName = name;
  }

  async firstUpdated() {
    const model = await getPuzzle(this._puzzleName);
    this._displayName = model.name;
    this._description = model.description;

    if (model.hasDefaultBoards) {
      this._presets = Array.from(model.difficultyLevels.entries()).map(([label, options]) => {
        const display = model.displayLabel(label);
        const split = splitPresetLabel(display);
        return { kind: 'board', label: display, options, sizeLabel: split.sizeLabel, detail: split.detail ?? '' };
      });
    } else {
      await initialisePuzzleMetadata(this._levels);
      const enginePresets = this._levels.get(this._puzzleName) ?? [];
      this._presets = enginePresets.map((preset) => {
        const split = splitPresetLabel(preset.name);
        return { kind: 'engine', presetId: preset.id, sizeLabel: split.sizeLabel, detail: split.detail ?? '' };
      });
    }

    const saved = await loadPresets(this._puzzleName);
    const catalog = await getFullCatalog();
    const definitions = catalog?.[this._puzzleName] ?? [];
    this._saved = (saved ?? []).map((preset: Preset) => {
      const summary = autoName(this._puzzleName, preset.options, definitions);
      const split = splitPresetLabel(summary);
      return { sizeLabel: split.sizeLabel, detail: split.detail ?? '', options: preset.options };
    });

    this._loading = false;
  }

  private onPresetPlay(preset: PresetRowView): void {
    if (preset.kind === 'engine') {
      navigate(`/puzzles/${this._puzzleName}/${preset.presetId}`);
      return;
    }
    navigate(
      `/puzzles/${this._puzzleName}?custom=${encodePresetOptions(preset.options)}&label=${encodeURIComponent(preset.label)}`
    );
  }

  private onSavedPlay(saved: SavedPresetView): void {
    navigate(`/puzzles/${this._puzzleName}?saved=${encodePresetOptions(saved.options)}`);
  }

  private onConfigure(): void {
    navigate(`/puzzles/${this._puzzleName}/custom`);
  }

  content() {
    const artSize = THUMB_ART[this._breakpoint];

    return html`
      <main class="app-picker app-picker--${this._puzzleName}">
        <section class="app-picker__identity">
          <div class="app-picker__thumb">
            <luna-board-art puzzle=${this._puzzleName} size=${artSize}></luna-board-art>
          </div>
          <div class="app-picker__title-block">
            <h1 class="mbw-type-title-screen mbw-type-title-screen-sm">${this._displayName}</h1>
            <p class="mbw-type-body-default">${this._description}</p>
          </div>
        </section>

        ${this._loading
          ? html`
              <div
                class="app-picker__skeleton"
                role="status"
                aria-label="Loading boards"
              >
                <luna-skeleton class="app-picker__skeleton-row"></luna-skeleton>
                <luna-skeleton class="app-picker__skeleton-row"></luna-skeleton>
                <luna-skeleton class="app-picker__skeleton-row"></luna-skeleton>
                <luna-skeleton class="app-picker__skeleton-row"></luna-skeleton>
              </div>
            `
          : html`
              <section class="app-picker__list" aria-label="Choose a board">
                <span class="app-picker__eyebrow mbw-type-eyebrow mbw-type-eyebrow-sm"
                  >Choose a board</span
                >

                ${this._presets.map(
                  (preset) => html`
                    <luna-preset-row
                      size-label=${preset.sizeLabel}
                      detail=${preset.detail ?? ''}
                      @luna-play=${() => this.onPresetPlay(preset)}
                    ></luna-preset-row>
                  `
                )}

                <luna-custom-board-card @luna-configure=${this.onConfigure}></luna-custom-board-card>

                <div class="app-picker__presets-title">
                  <span class="app-picker__accent" aria-hidden="true"></span>
                  <span class="mbw-type-title-section mbw-type-title-section-sm">My presets</span>
                </div>

                ${this._saved.length === 0
                  ? html`<p class="app-picker__empty mbw-type-body-small"
                      >Your custom games will appear here.</p
                    >`
                  : this._saved.map(
                      (saved) => html`
                        <luna-preset-row
                          size-label=${saved.sizeLabel}
                          detail=${saved.detail}
                          @luna-play=${() => this.onSavedPlay(saved)}
                        ></luna-preset-row>
                      `
                    )}
              </section>
            `}
      </main>
    `;
  }

  styles() {
    return css`
      .app-picker {
        box-sizing: border-box;
        display: flex;
        gap: 34px;
        padding: 30px 40px 40px; /* pinned: figma Picker desktop */
      }

      .app-picker__identity {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 260px;
        flex-shrink: 0;
      }

      .app-picker__thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 260px;
        height: 260px;
        box-sizing: border-box;
        padding: 22px;
        border-radius: 18px;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04), inset 0 2px 6px rgba(0, 0, 0, 0.06);
      }

      .app-picker__title-block {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .app-picker__title-block h1 {
        margin: 0;
        color: var(--mbw-text-ink);
      }

      .app-picker__title-block p {
        margin: 0;
        color: var(--mbw-text-body);
      }

      .app-picker__list {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .app-picker__skeleton {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .app-picker__skeleton-row {
        height: 84px;
        border-radius: var(--luna-radius-card-sm);
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
      }

      .app-picker__skeleton-row:nth-child(2) {
        animation-delay: 0.15s;
      }

      .app-picker__skeleton-row:nth-child(3) {
        animation-delay: 0.3s;
      }

      .app-picker__skeleton-row:nth-child(4) {
        animation-delay: 0.45s;
      }

      .app-picker__eyebrow {
        color: var(--mbw-text-muted);
      }

      .app-picker__list luna-preset-row,
      .app-picker__list luna-custom-board-card {
        width: 100%;
      }

      .app-picker__presets-title {
        display: flex;
        align-items: center;
        gap: 10px;
        padding-top: 18px;
      }

      .app-picker__accent {
        width: 3px;
        height: 18px;
        border-radius: 2px;
      }

      .app-picker__presets-title span:last-child {
        color: var(--mbw-text-ink);
      }

      .app-picker__empty {
        color: var(--mbw-text-body);
        margin: 0;
      }

      .app-picker--map .app-picker__thumb {
        background: var(--mbw-palette-map-tint);
      }
      .app-picker--map .app-picker__accent {
        background: var(--mbw-palette-map-accent);
      }
      .app-picker--pegs .app-picker__thumb {
        background: var(--mbw-palette-pegs-tint);
      }
      .app-picker--pegs .app-picker__accent {
        background: var(--mbw-palette-pegs-accent);
      }
      .app-picker--rectangles .app-picker__thumb {
        background: var(--mbw-palette-rectangles-tint);
      }
      .app-picker--rectangles .app-picker__accent {
        background: var(--mbw-palette-rectangles-accent);
      }
      .app-picker--tents .app-picker__thumb {
        background: var(--mbw-palette-tents-tint);
      }
      .app-picker--tents .app-picker__accent {
        background: var(--mbw-palette-tents-accent);
      }
      .app-picker--unruly .app-picker__thumb {
        background: var(--mbw-palette-unruly-tint);
      }
      .app-picker--unruly .app-picker__accent {
        background: var(--mbw-palette-unruly-accent);
      }
      .app-picker--untangle .app-picker__thumb {
        background: var(--mbw-palette-untangle-tint);
      }
      .app-picker--untangle .app-picker__accent {
        background: var(--mbw-palette-untangle-accent);
      }

      @media (max-width: 1279px) {
        .app-picker {
          flex-direction: column;
          gap: 20px;
        }

        .app-picker__identity {
          flex-direction: row;
          align-items: center;
          gap: 20px;
          width: 100%;
        }

        .app-picker__thumb {
          width: 100px;
          height: 100px;
          padding: 12px;
          border-radius: 16px;
        }
      }

      @media (max-width: 767px) {
        .app-picker {
          gap: 14px;
          padding: 30px 16px 24px; /* pinned: figma Picker mobile */
        }

        .app-picker__identity {
          gap: 14px;
        }

        .app-picker__thumb {
          width: 72px;
          height: 72px;
          padding: 8px;
          border-radius: 14px;
        }

        .app-picker__list {
          gap: 8px;
        }
      }
    `;
  }
}
